import { useCallback, useEffect, useState } from "react";
import { Color } from "@material-ui/lab/Alert/Alert";
import { useStoreSelector } from "../redux/useStoreSelector";
import { dispatcher } from "../../store";
import { ApiError, ResponseWithBody } from "../../api/api";

const NOTIFY_DELAY = 8 * 1000;

type ApiRes<T> =
  | ResponseWithBody<200, T>
  | ResponseWithBody<400, ApiError>
  | ResponseWithBody<401, void>
  | ResponseWithBody<403, void>
  | ResponseWithBody<404, void>
  | ResponseWithBody<422, void>
  | ResponseWithBody<500, void>;

export interface Notify {
  title: string;
  variant: Color;
  message?: string;
}

export function useNotify() {
  const notifies = useStoreSelector((s) => s.system.notifies),
    [current, setCurrent] = useState<Notify>();

  const addNotify = useCallback(
    (variant: Color, title: string, message?: string) => {
      const n: Notify = { variant, title, message };
      dispatcher.system.addNotifies(n);
    },
    []
  );

  const handleApiError = useCallback(
    <T>(res: ApiRes<T>): T | undefined => {
      if (res.status === 200) {
        return (res as ResponseWithBody<200, T>).body;
      } else if (res.status === 400) {
        const resError = res as ResponseWithBody<400, ApiError>;
        addNotify("error", "Error", resError.body.messages?.join("\n"));
      } else {
        addNotify("error", "Unknown error");
      }
    },
    [addNotify]
  );

  const onClose = useCallback(() => {
    setCurrent(undefined);
  }, []);

  useEffect(() => {
    if (notifies.length > 0 && !current) {
      const [head, ...tail] = notifies;
      setCurrent(head);
      dispatcher.system.setNotifies(tail);
    }
  }, [current, notifies]);

  useEffect(() => {
    if (current) {
      const id = setTimeout(() => setCurrent(undefined), NOTIFY_DELAY);
      return () => clearTimeout(id);
    }
  }, [current]);

  return { current, addNotify, handleApiError, onClose };
}
